import React, { useCallback, useMemo, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SectionCard from '../../../components/SectionCard';
import StatCard from '../../../components/StatCard';
import StatusChip from '../../../components/StatusChip';
import InvButton from '../../../components/InvButton';
import { getControlledQueue } from '../../../api/pharmacy';
import { mapList, mapControlledItem } from '../../../utils/pharmacyMappers';
import { groupControlledOrders } from '../../../utils/controlledDrug';
import usePharmacyLoad from '../../../hooks/usePharmacyLoad';
import useRefreshOnFocus from '../../../hooks/useRefreshOnFocus';
import { IPD_COLORS } from '../../../theme';
import { fadeInSx } from '../pharmacyStyles';
import PharmacyTableState from '../PharmacyTableState';
import { formatDateTime } from '../../../utils';

/**
 * The counter's side of the controlled-drug gate: every order parked with the
 * pharmacist, and where each one stands.
 *
 * Deliberately has no timer. A cashier parks an order and serves the next
 * customer; they come back here when they are ready, and the list refreshes on
 * arrival, on window focus, and on demand. Background polling was what made
 * the earlier build hammer the API for nothing.
 */
export default function ControlledOrdersTab() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const [refreshedAt, setRefreshedAt] = useState(null);

  const { data, loading, error, reload } = usePharmacyLoad(
    // No status filter: an order still needing attention includes its already
    // AUTHORIZED and DENIED lines, not just the pending ones.
    () => getControlledQueue({ status: undefined }),
    [],
  );

  const refresh = useCallback(() => {
    setRefreshedAt(new Date().toISOString());
    return reload();
  }, [reload]);

  // Pick up decisions made in the other tab the moment the user looks back here.
  useRefreshOnFocus(refresh);

  const orders = useMemo(
    () => groupControlledOrders(mapList(data, mapControlledItem)),
    [data],
  );

  const waiting = orders.filter((o) => o.status === 'AWAITING_AUTHORIZATION').length;
  const ready = orders.filter((o) => o.status === 'AUTHORIZED').length;
  const denied = orders.filter((o) => o.status === 'REJECTED').length;

  function openOrder(order) {
    history.push(`${url}/${order.orderId}`);
  }

  return (
    <Box sx={fadeInSx}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 2.5 }}>
        <StatCard label="Waiting for pharmacist" value={waiting} icon={LockIcon} tone="amber" loading={loading} />
        <StatCard label="Ready to collect" value={ready} icon={LockIcon} tone="mint" loading={loading} />
        <StatCard label="Denied" value={denied} icon={LockIcon} tone="teal" loading={loading} />
      </Box>

      <SectionCard
        title="Controlled Orders"
        subtitle="Orders parked with the pharmacist. Open one once it is authorized to collect payment."
        actions={(
          <InvButton
            size="small"
            variant="outlined"
            tone="teal"
            startIcon={<RefreshIcon />}
            disabled={loading}
            onClick={refresh}
          >
            Refresh
          </InvButton>
        )}
      >
        <PharmacyTableState
          loading={loading}
          error={error}
          empty={!loading && !error && orders.length === 0}
          emptyMessage="No controlled orders open. Ones you send to the pharmacist appear here."
          onRetry={refresh}
        >
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${IPD_COLORS.border}` }}>
                  {['Order', 'Patient', 'Medicines', 'Authorized by', 'Status', 'Open'].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '10px 12px', fontSize: 11, color: IPD_COLORS.textSecondary, fontWeight: 700, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const approver = order.lines.find((l) => l.approved_by);
                  return (
                    <tr key={order.orderId} style={{ borderBottom: `1px solid ${IPD_COLORS.border}` }}>
                      <td style={{ padding: '12px' }}>
                        <Box sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 13 }}>
                          {order.orderNumber || '—'}
                        </Box>
                        <Box sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                          {order.sourceType === 'SALE' ? 'Counter sale' : 'IPD dispense'}
                        </Box>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <Box sx={{ fontWeight: 600 }}>{order.patientName || 'Not registered'}</Box>
                        {order.patientMobile && (
                          <Box sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>{order.patientMobile}</Box>
                        )}
                      </td>
                      <td style={{ padding: '12px', fontSize: 13 }}>
                        {order.lines.map((line) => (
                          <Box key={line.id} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.25 }}>
                            <span>{line.medicine || '—'}</span>
                            <span style={{ color: IPD_COLORS.textSecondary }}>× {line.qty}</span>
                            <StatusChip status={line.status} size="sm" />
                          </Box>
                        ))}
                      </td>
                      <td style={{ padding: '12px', fontSize: 13 }}>
                        {approver ? (
                          <>
                            <Box>{approver.approved_by}</Box>
                            {approver.approved_at && (
                              <Box sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                                {formatDateTime(approver.approved_at)}
                              </Box>
                            )}
                          </>
                        ) : '—'}
                      </td>
                      <td style={{ padding: '12px' }}><StatusChip status={order.status} /></td>
                      <td style={{ padding: '12px' }}>
                        <Tooltip title="Open this order">
                          <IconButton size="small" sx={{ color: IPD_COLORS.primary }} onClick={() => openOrder(order)}>
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        </PharmacyTableState>

        <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary, mt: 1.5 }}>
          {refreshedAt
            ? `Last refreshed ${formatDateTime(refreshedAt)} · refreshes when you return to this tab`
            : 'Refreshes when you return to this tab'}
        </Typography>
      </SectionCard>
    </Box>
  );
}
